<script setup lang="ts">
import { UiText, UiIcon, UiTooltip, UiAvatar } from "@shopexperts/shared";
import type { CompactUser } from "@shopexperts/legacy-api";
import type { RouteLocationRaw } from "vue-router";
import { partyName } from "./transactionParty";

interface SummaryStat {
  key: string;
  label: string;
  value: string;
  valueClass: string;
}

defineProps<{
  transactionId: number;
  kindLabel: string;
  fullDate: string;
  dateTimeLabel: string;
  expertUser: CompactUser | null;
  clientUser: CompactUser | null;
  expertRoute: RouteLocationRaw | null;
  stats: SummaryStat[];
  expanded: boolean;
  isDeleted?: boolean;
  deletedSummaryLabel?: string | null;
}>();
</script>

<template>
  <div class="flex items-start justify-between gap-4 px-4 py-3" :class="isDeleted ? 'bg-red-50/80' : ''">
    <div class="min-w-0">
      <div class="flex min-w-0 items-center gap-2">
        <UiText
          as="span"
          variant="p-small"
          :text="`#${transactionId}`"
          class="shrink-0 font-semibold tabular-nums text-se-gray-500"
        />
        <UiText
          as="span"
          variant="p"
          :text="kindLabel"
          class="min-w-0 truncate font-semibold capitalize"
          :class="isDeleted ? 'text-red-900 line-through decoration-2' : 'text-se-dark'"
        />
        <span
          v-if="isDeleted"
          class="inline-flex shrink-0 items-center gap-1 rounded-full border border-red-300 bg-red-100 px-2 py-0.5"
        >
          <UiIcon name="delete" class="h-3 w-3 text-red-700" />
          <UiText as="span" variant="caption" text="Deleted" class="font-semibold uppercase text-red-800" />
        </span>
      </div>

      <div class="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <UiTooltip :content="fullDate">
          <UiText as="span" variant="caption-normal" :text="dateTimeLabel" class="text-se-gray-500" />
        </UiTooltip>

        <div v-if="clientUser || expertUser" class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <RouterLink
            v-if="expertRoute && expertUser"
            :to="expertRoute"
            class="group/expert flex min-w-0 items-center gap-1.5 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-se-dark"
            :aria-label="`Open ${partyName(expertUser)}'s expert profile`"
            @click.stop
            @keydown.stop
          >
            <UiAvatar
              size="inline"
              :responsive="false"
              color-coded
              :name="expertUser.name ?? undefined"
              :url="expertUser.avatar ?? undefined"
            />
            <UiText
              as="span"
              variant="caption-normal"
              :text="partyName(expertUser)"
              class="min-w-0 truncate font-semibold text-se-dark group-hover/expert:underline"
            />
          </RouterLink>
          <div v-else-if="expertUser" class="flex min-w-0 items-center gap-1.5">
            <UiAvatar
              size="inline"
              :responsive="false"
              color-coded
              :name="expertUser.name ?? undefined"
              :url="expertUser.avatar ?? undefined"
            />
            <UiText
              as="span"
              variant="caption-normal"
              :text="partyName(expertUser)"
              class="min-w-0 truncate font-semibold text-se-dark"
            />
          </div>

          <UiIcon
            v-if="clientUser && expertUser"
            name="chevron-down"
            aria-hidden="true"
            class="size-3.5 shrink-0 rotate-90 text-se-gray-300"
          />

          <div v-if="clientUser" class="flex min-w-0 items-center gap-1.5">
            <UiAvatar
              size="inline"
              :responsive="false"
              color-coded
              :name="clientUser.name ?? undefined"
              :url="clientUser.avatar ?? undefined"
            />
            <UiText
              as="span"
              variant="caption-normal"
              :text="partyName(clientUser)"
              class="min-w-0 truncate text-se-gray-600"
            />
          </div>
        </div>

        <UiText
          v-if="deletedSummaryLabel"
          as="span"
          variant="caption-normal"
          :text="deletedSummaryLabel"
          class="font-semibold text-red-700"
        />
      </div>
    </div>

    <div class="flex shrink-0 items-center gap-4">
      <div v-if="stats.length" class="flex flex-wrap items-end justify-end gap-x-4 gap-y-2">
        <div
          v-for="stat in stats"
          :key="stat.key"
          class="flex min-w-[4.5rem] flex-col items-end gap-1"
          :class="stat.key === 'expert_share' ? 'border-l border-se-gray-200 pl-4' : ''"
        >
          <UiText
            as="span"
            variant="caption"
            :text="stat.label"
            class="whitespace-nowrap uppercase tracking-wide text-se-gray-400"
          />
          <UiText as="span" variant="p-small" :text="stat.value" class="tabular-nums" :class="stat.valueClass" />
        </div>
      </div>
      <UiIcon
        name="chevron-down"
        class="h-4 w-4 shrink-0 text-se-gray-400 transition-transform duration-300"
        :class="expanded ? 'rotate-180 text-se-dark' : 'group-hover/row:text-se-dark'"
      />
    </div>
  </div>
</template>
